import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import GlobalFlowIcon from 'garaje/components/global-flow-icon';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import LoadMore from 'garaje/components/visitors/questionnaires/list/load-more';
import QuestionnairesListLoader, { type Filters } from 'garaje/components/visitors/questionnaires/list/loader';
import preventDefault from 'garaje/helpers/prevent-default';
import type Company from 'garaje/models/company';
import type Location from 'garaje/models/location';
import type Questionnaire from 'garaje/models/questionnaire';
import { TrackedSet } from 'tracked-built-ins';

interface QuestionnairesSelectorModalSignature {
  Args: {
    assignedQuestionnaires: Questionnaire[];
    company?: Company;
    location?: Location;

    addQuestionnaires: (questionnaires: Questionnaire[]) => void;
    close: () => void;
  };
  Element: HTMLFormElement;
}

const filters: Filters = {
  'include-global': true,
};

export default class QuestionnairesSelectorModal extends Component<QuestionnairesSelectorModalSignature> {
  #selectedQuestionnaires = new TrackedSet<Questionnaire>();

  get noQuestionnairesSelected(): boolean {
    return this.#selectedQuestionnaires.size === 0;
  }

  @action
  addQuestionnaires(): void {
    this.args.addQuestionnaires([...this.#selectedQuestionnaires]);
  }

  @action
  alreadyHasQuestionnaire(questionnaire: Questionnaire): boolean {
    return this.args.assignedQuestionnaires.includes(questionnaire);
  }

  /**
   * Decide whether to show the "global" icon for this questionnaire. This should happen if we're in a location context
   * (meaning a `@location` arg was given) and the questionnaire is not assigned to the location (meaning it's global).
   */
  @action
  showGlobalIconFor(questionnaire: Questionnaire): boolean {
    // if we weren't given a `@location`, dont't show "global" icon for any questionnaires
    const location = this.args.location;
    if (!location) return false;

    // otherwise, check see whether the questionnaire belongs to the location; if not, show "global" icon
    return questionnaire.belongsTo('location').id() !== location.id;
  }

  @action
  toggleSelected(questionnaire: Questionnaire): void {
    if (this.#selectedQuestionnaires.has(questionnaire)) {
      this.#selectedQuestionnaires.delete(questionnaire);
    } else {
      this.#selectedQuestionnaires.add(questionnaire);
    }
  }

  <template>
    <ModalDialog @clickOutsideToClose={{false}} @targetAttachment="center" @translucentOverlay={{true}}>
      <form class="p-8" {{on "submit" (preventDefault this.addQuestionnaires)}} ...attributes>
        <QuestionnairesListLoader @company={{@company}} @location={{@location}} @filters={{filters}} @sort="title">
          <:list as |questionnaires loader|>
            <h3 class="mb-4">Select assessments to apply:</h3>
            <ul class="mb-4">
              {{#each questionnaires as |questionnaire|}}
                {{#let (this.alreadyHasQuestionnaire questionnaire) as |alreadyHasQuestionnaire|}}
                  <li class="p-1" data-test-questionnaire={{questionnaire.id}}>
                    <input
                      type="checkbox"
                      checked={{alreadyHasQuestionnaire}}
                      disabled={{alreadyHasQuestionnaire}}
                      {{on "change" (fn this.toggleSelected questionnaire)}}
                      data-test-select-questionnaire={{questionnaire.id}}
                    />
                    <GlobalFlowIcon @show={{this.showGlobalIconFor questionnaire}} @tooltipText="Global assessment">
                      {{questionnaire.title}}
                    </GlobalFlowIcon>
                  </li>
                {{/let}}
              {{/each}}
              <li>
                <LoadMore @loader={{loader}} />
              </li>
            </ul>

            <button
              type="submit"
              class="btn btn--brand mr-4"
              disabled={{this.noQuestionnairesSelected}}
              data-test-add-button
            >Add</button>
            <button type="button" class="btn btn--outline" {{on "click" @close}} data-test-cancel-button>Cancel</button>
          </:list>
        </QuestionnairesListLoader>
      </form>
    </ModalDialog>
  </template>
}
